// Lädt EIN einzelnes Match anhand seiner ID - für BetmanStoryWidget (und potenziell überall dort,
// wo Details zu einem Match gezeigt werden sollen, ohne den kompletten Spielplan zu laden). Nutzt
// dieselbe mapMatch-Normalisierung wie der große Spielplan-Request, damit das Ergebnis 1:1
// kompatibel mit DetailOverlay ist.
//
// ANNAHME, bitte mit echtem Backend abgleichen: Endpunkt liefert EIN Match-Objekt in derselben
// Rohstruktur wie ein einzelner Eintrag aus get-gameplan-from-to (nicht in Tages-Gruppen
// verschachtelt) - falls das Format abweicht, muss hier nachjustiert werden.
import { mapMatch, mapTeamSpiel } from './mapping.js'

const matchCache = new Map() // matchId -> Promise<Match>

export function ladeEinzelnesMatch(apiBase, matchId, flaggenBasisUrl = 'https://app.team-match.io') {
  if (matchCache.has(matchId)) return matchCache.get(matchId)

  const promise = fetch(`${apiBase}/widget/get-match-details?matchId=${encodeURIComponent(matchId)}`)
    .then(res => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    })
    .then(raw =>   {
      const m = mapMatch(raw.match, flaggenBasisUrl)
      m.spieleHeim = (raw['matches-team-home'] ?? []).map(mapTeamSpiel)
      m.spieleAuswaerts = (raw['matches-team-away'] ?? []).map(mapTeamSpiel)
      return m
    })
    .catch(err => {
      matchCache.delete(matchId) // bei Fehler nicht cachen - erneuter Versuch soll möglich bleiben
      throw err
    })

  matchCache.set(matchId, promise)
  return promise
}

// Lädt die Tabelle für EINE Liga (statt der Tabellen ALLER Ligen aus dem großen Spielplan-
// Request) - gecacht nach ligaId, nicht nach matchId: zwei Matches aus derselben Liga teilen
// sich denselben Cache-Eintrag, kein doppelter Request nötig.
//
// ANNAHME, bitte mit echtem Backend abgleichen: Endpunkt-Pfad und Rückgabeformat geraten -
// Rückgabe sollte dieselbe Struktur haben wie ein Eintrag im "groups"-Array aus
// get-gameplan-from-to, damit tabelleFuerLiga()/TabelleContent.vue unverändert weiterfunktionieren.
const tabelleCache = new Map() // ligaId -> Promise<Array> (Tabellen-Blöcke für diese Liga)

// Übersetzt die von der API gelieferten Gruppen-Bezeichnungen ins Deutsche - deckt "League A/B/
// C/D" -> "Liga A/B/C/D" und "Group N" -> "Gruppe N" ab (Regex statt fixer Zuordnung, damit auch
// künftige Liga-Stufen/Gruppennummern automatisch erfasst werden, nicht nur die aktuell bekannten).
function uebersetzeGruppenName(group) {
  if (typeof group !== 'string') return group
  return group
    .replace(/\bLeague ([A-D])\b/g, 'Liga $1')
    .replace(/\bGroup (\d+)\b/g, 'Gruppe $1')
}

export function ladeTabelleFuerLiga(apiBase, ligaId) {
  if (ligaId == null) return Promise.resolve([])
  if (tabelleCache.has(ligaId)) return tabelleCache.get(ligaId)

  const promise = fetch(`${apiBase}/widget/get-standings?ligaId=${encodeURIComponent(ligaId)}`)
    .then(res => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    })
    .then(raw => Array.isArray(raw) ? raw : (Array.isArray(raw?.groups) ? raw.groups : []))
    .then(gruppen => gruppen.map(gruppe => ({
      ...gruppe,
      standings: Array.isArray(gruppe.standings)
        ? gruppe.standings.map(eintrag => ({ ...eintrag, group: uebersetzeGruppenName(eintrag.group) }))
        : gruppe.standings
    })))
    .catch(err => {
      tabelleCache.delete(ligaId)
      throw err
    })

  tabelleCache.set(ligaId, promise)
  return promise
}